import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { Container } from "../../components/Container";
import { Title } from "../../components/Title";
import { SubmitButton, BackButton } from "../../components/Button";
import { TextInput, TextArea } from "../../components/TextInput";
import { OrderSidebar } from "../../components/Sidebar";
import UserUtils from "../../util/UserUtils";
import {
  getOrderById,
  updateOrder,
  getOrderHistory,
  updateOrderHistory,
  resendEmail
} from "../../api/AWSAPI";
import useStatus, { orderStatusColor } from "../../hooks/useStatus";
import { ORDER_STATUS } from "../../constants/orderConstants";
import ApiClient from "../../api/client";
import { Loader } from "../../components/Loader";
import APIUtils from "../../api/APIUtils";
import { UserRole, hasRole } from "../../util/roles";

const NON_ADMIN_FUNDING_LIMIT = 5000;

function OrderPage() {
  const navigate = useNavigate();
  const apiClient = new ApiClient(process.env.REACT_APP_AWS_URL);

  const { id } = useParams();
  const [orderNumber, setOrderNumber] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [clientName, setClientName] = useState("");
  const [orderStatus, setOrderStatus] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [discountValue, setDiscountValue] = useState(0);
  const [discountRate, setDiscountRate] = useState(0);
  const [orderItems, setOrderItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [habitatDiscountValue, setHabitatDiscountValue] = useState(0);
  const [habitatDiscountRate, setHabitatDiscountRate] = useState(0);
  const [sainsburysDiscountValue, setSainsburysDiscountValue] = useState(0);
  const [sainsburysDiscountRate, setSainsburysDiscountRate] = useState(0);
  const [multiDiscountValue, setMultiDiscountValue] = useState(0);
  const [multiDiscountRate, setMultiDiscountRate] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [userRoles, setUserRoles] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [reModal, setReModal] = useState(false);
  const [reEmail, setReEmail] = useState("");
  const [confirmedByName, setConfirmedByName] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendEmailError, setResendEmailError] = useState("");
  const [totalDeliveryAmount, setTotalDeliveryAmount] = useState(0);
  const [orderNet, setOrderNet] = useState(0);
  const [addedDelivery, setAddedDelivery] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const { statuses } = useStatus();

  useEffect(() => {
    loadUser();
    loadOrder();
    loadHistory();
  }, []);

  const loadHistory = () => {
    getOrderHistory(id, (data) => {
      if (data?.success && data.items.length > 0) {
        setHistory(
          data.items.sort(
            (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp)
          )
        );
        setNoteBody("");
      }
    });
  };

  const onSubmitResendEmail = () => {
    setIsResending(true);
    setResendEmailError("");
    resendEmail(id, reEmail, (responseBody) => {
      if (responseBody?.success) {
        const historyPayload = {
          orderId: id,
          timeStamp: moment().toISOString(),
          comments: `Resent order confirmation email to ${reEmail} by ${confirmedByName}`,
          dataType: "EMAIL_RESEND",
        };
        updateOrderHistory(historyPayload, (historyResponse) => {
          if (historyResponse?.success) {
            loadHistory();
          }
          setReModal(false);
          setIsResending(false);
        });
      } else {
        setResendEmailError("Failed to resend email. Please try again.");
        setIsResending(false);
      }
    });
  };

  const onCloseResendModal = () => {
    setReModal(false);
    setResendEmailError("");
    setIsResending(false);
  };

  const showResendEmailModal = () => {
    setResendEmailError("");
    setReModal(true);
  };

  const loadUser = () => {
    UserUtils.getUserData((data) => {
      if (data) { 
        setUserRoles(data.roles || []);
        setConfirmedByName(
          `${data.name || ""}`.trim()
        );
      } else {
        navigate("/");
      }
    });
  };

  const loadOrder = () => {
    setIsLoading(true);
    getOrderById(id, (data) => {
      if (data?.success && data.order.length > 0) {
        const order = data.order[0];
        setClientName(order.contactCompany);
        setOrderNumber(order.orderNumber);
        setOrderDate(order.orderDate);
        setOrderStatus(order.orderStatus);
        setReEmail(order.contactEmail);
        setTotalCost(order.orderTotal);
        setOrderItems(order.items);
        setOrderNet(order.orderNet);
        setTotalDeliveryAmount(order.totalDeliveryAmount);
        setAddedDelivery(order.addedDelivery);

        order.discounts?.forEach((item) => {
          switch (item.productBrand) {
            case "argos":
              setDiscountValue(Number(item.discountAmount));
              setDiscountRate(Number(item.discountRate));
              break;
            case "sainsburys":
              setSainsburysDiscountValue(Number(item.discountAmount));
              setSainsburysDiscountRate(Number(item.discountRate));
              break;
            case "habitat":
              setHabitatDiscountValue(Number(item.discountAmount));
              setHabitatDiscountRate(Number(item.discountRate));
              break;
            case "multibrand":
              setMultiDiscountValue(Number(item.discountAmount));
              setMultiDiscountRate(Number(item.discountRate));
              break;
            default:
              break;
          }
        });

        const totalDiscount = (order.discounts || []).reduce(
          (sum, item) => sum + Number(item.discountAmount),
          0
        );
        setDiscount(totalDiscount);
      }
      setIsLoading(false);
    });
  };

  const cancelOrder = () => {
    const payload = {
      orderStatus: 4,
    };

    updateOrder(id, payload, (responseBody) => {
      if (responseBody?.success) {
        const historyPayload = {
          orderId: id,
          timeStamp: moment().toISOString(),
          comments: `Order cancelled by ${confirmedByName}`,
          dataType: "CANCELLED",
        };
        updateOrderHistory(historyPayload, (historyResponse) => {
          if (historyResponse?.success) {
            navigate(`/orders/${id}`);
          } else {
            setApiError("Failed to cancel order history");
          }
        });
      } else {
        setApiError("Failed to cancel order");
      }
    });
  };

  const fundsReceived = () => {
    navigate(`/orders/funds/${id}`);
  };

  const editOrder = () => {
    navigate(`/orders/edit/${id}`);
  };

  const cancelFundsReceived = async () => {
    let previousStatus;
    try {
      const response = await apiClient.get(`/${APIUtils.GATE_API}/NAGCO/orderhistory/${id}`, {
        headers: await APIUtils.getAuthorizationHeader(),
      });

      if (!response.success) {
        throw new Error(
          `Failed to update order: ${
            response.data.errorTxt || response.message
          }`
        );
      }

      const loggedItems = response.data.items;
      const fundedItems = loggedItems.filter(
        (item) => item.dataType === "CALLBACK" // Looking for the callback log from payment handler
      );

      if (fundedItems.length === 0) {
        previousStatus = ORDER_STATUS.AWAITING_PAYMENT;
      } else {
        previousStatus = ORDER_STATUS.PAYMENT_MADE;
      }
    } catch (error) {
      console.error("Error updating order:", error);
      throw error;
    }

    const payload = {
      orderStatus: previousStatus,
      paymentConfirmedBy: confirmedByName,
      paymentMethod: null,
      paymentReference: null,
      paymentReceivedDate: null,
      confirmedDateTime: null,
    };

    try {
      const response = await apiClient.put(`/${APIUtils.GATE_API}/NAGCO/order/${id}`, payload, {
        headers: await APIUtils.getAuthorizationHeader(),
      });

      if (!response.success) {
        throw new Error(
          `Failed to update order: ${
            response.data.errorTxt || response.message
          }`
        );
      }
    } catch (error) {
      console.error("Error updating order:", error);
      throw error;
    }

    try {
      await apiClient.post(
        `/${APIUtils.GATE_API}/NAGCO/orderhistory`,
        {
          orderId: id,
          timestamp: moment().toISOString(),
          comments: `Funding confirmation cancelled by ${confirmedByName}`,
          dataType: "FUNDED",
        },
        {
          headers: await APIUtils.getAuthorizationHeader(),
        }
      );
    } catch (error) {
      console.error("Error logging order history:", error);
      setError("An error occurred while logging order history.");
      return;
    }

    loadOrder();
    loadHistory();
  };

  const duplicate = () => {
    navigate(`/orders/edit/new?copy=${id}`);
  };

  const addNote = () => {
    if (noteBody !== "" && confirmedByName) {
      const prefixedNote = `${confirmedByName}: ${noteBody}`;

      const payload = {
        orderId: id,
        timeStamp: moment().toISOString(),
        comments: prefixedNote,
        dataType: "NOTE",
      };
      updateOrderHistory(payload, (historyResponse) => {
        loadHistory();
      });
    }
  };

  if (error) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-sm-12 content">
            <BackButton title="Back" url="/orders" />
            <p className="text-danger">{error}</p>
            <p>If this issue persists, please contact support.</p>
          </div>
        </div>
      </div>
    );
  }

    if (isLoading) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-sm-12 content">
            <BackButton title="Back" url="/orders" />
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row">
        <div className="col-sm-12 content">
          <BackButton title="Back" url="/orders" />
          <Title title={`${orderNumber} - ${clientName}`} />
          <div className="row">
            <div className="col-sm-8">
              <span
                className={`${
                  orderStatusColor[orderStatus] || "unknown-warning"
                }`}
              >
                {statuses[orderStatus]?.orderStatusText || "Unknown"}
              </span>{" "}
              Created: {moment(orderDate).format("dddd DD MMMM YYYY - HH:mm")}
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12 order-buttons" style={{ paddingTop: 20 }}>
          {hasRole(userRoles, UserRole.Admin, UserRole.NagcoMaintainer) && orderStatus === ORDER_STATUS.AWAITING_PAYMENT && (
            <div style={{ display: "inline", marginRight: 20 }}>
              <SubmitButton text="Change Order" onClick={editOrder} />
            </div>
          )}

          {hasRole(userRoles, UserRole.Admin, UserRole.NagcoMaintainer) && id !== "new" && (
            <div style={{ display: "inline", marginRight: 20 }}>
              <SubmitButton text="Copy Order" onClick={duplicate} />
            </div>
          )}

          {hasRole(userRoles, UserRole.Admin, UserRole.NagcoMaintainer) && orderStatus === ORDER_STATUS.AWAITING_PAYMENT && (
            <div style={{ display: "inline", marginRight: 20 }}>
              <SubmitButton
                text="Cancel Order"
                onClick={(e) => {
                  if (
                    window.confirm(
                      "Are you sure you wish to cancel this order?"
                    )
                  )
                    cancelOrder();
                }}
              />
            </div>
          )}

          {hasRole(userRoles, UserRole.Admin, UserRole.NagcoReceiptAll, UserRole.NagcoReceiptUnder5000) && orderStatus === ORDER_STATUS.PAYMENT_RECEIVED && (
            <div style={{ display: "inline", marginRight: 20 }}>
              <SubmitButton
                text="Cancel Receipt"
                onClick={cancelFundsReceived}
                disabled={hasRole(userRoles, UserRole.NagcoReceiptUnder5000) && !hasRole(userRoles, UserRole.Admin, UserRole.NagcoReceiptAll) && totalCost - discount >= NON_ADMIN_FUNDING_LIMIT}
              />
            </div>
          )}

          {hasRole(userRoles, UserRole.Admin, UserRole.NagcoMaintainer) && orderStatus === ORDER_STATUS.AWAITING_PAYMENT && (
            <div style={{ display: "inline", marginRight: 20 }}>
              <SubmitButton
                text="Resend Email"
                onClick={() => showResendEmailModal()}
              />
            </div>
          )}

          {([ORDER_STATUS.AWAITING_PAYMENT, ORDER_STATUS.PAYMENT_MADE].includes(orderStatus)) &&
            hasRole(userRoles, UserRole.Admin, UserRole.NagcoReceiptUnder5000, UserRole.NagcoReceiptAll) && (
              <div style={{ display: "inline", marginRight: 20 }}>
                <SubmitButton
                  text="Funds Received"
                  onClick={fundsReceived}
                  disabled={hasRole(userRoles, UserRole.NagcoReceiptUnder5000) && !hasRole(userRoles, UserRole.Admin, UserRole.NagcoReceiptAll) && totalCost - discount >= NON_ADMIN_FUNDING_LIMIT}
                />
              </div>
            )}
        </div>
      </div>
      <div className="row row-eq-height">
        <div className="col-sm-8 content">
          <Container>
            <div className="row">
              <div className="col-sm-12">
                <h4 style={{ fontWeight: "bold", color: "#333" }}>
                  Order Details
                </h4>

                <table>
                  <thead>
                    <tr>
                      <th width="50%">Product Type</th>
                      <th width="25%">Card Quantity</th>
                      <th width="25%">Card Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderItems.map((item) => (
                      <tr key={`${item.productName}-${item.quantity}`}>
                        <td>{item.productName}</td>
                        <td>{item.quantity}</td>
                        <td>&pound;{item.amount.toFixed(2)}</td>
                      </tr>
                    ))}

                    <tr style={{ borderBottom: 0 }}>
                      <td>&nbsp;</td>
                      <td>Cost:</td>
                      <td>&pound;{Number.parseFloat(totalCost).toFixed(2)}</td>
                    </tr>

                    {orderItems.length > 0 && addedDelivery === true && (
                      <tr style={{ borderBottom: 0 }}>
                        <td>&nbsp;</td>
                        <td>Delivery Charge:</td>
                        <td>&pound;{Number.parseFloat(totalDeliveryAmount).toFixed(2)}</td>
                      </tr>
                    )}

                    {discountValue > 0 && (
                      <tr style={{ borderBottom: 0 }}>
                        <td>&nbsp;</td>
                        <td>
                          Argos Discount ({Number.parseFloat(discountRate).toFixed(2)}
                          %):
                        </td>
                        <td>&pound;{Number.parseFloat(discountValue).toFixed(2)}</td>
                      </tr>
                    )}
                    {sainsburysDiscountValue > 0 && (
                      <tr style={{ borderBottom: 0 }}>
                        <td>&nbsp;</td>
                        <td>
                          Sainsbury's Discount (
                          {Number.parseFloat(sainsburysDiscountRate).toFixed(2)}%):
                        </td>
                        <td>
                          &pound;
                          {Number.parseFloat(sainsburysDiscountValue).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {habitatDiscountValue > 0 && (
                      <tr style={{ borderBottom: 0 }}>
                        <td>&nbsp;</td>
                        <td>
                          Habitat Discount (
                          {Number.parseFloat(habitatDiscountRate).toFixed(2)}%):
                        </td>
                        <td>
                          &pound;{Number.parseFloat(habitatDiscountValue).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {multiDiscountValue > 0 && (
                      <tr style={{ borderBottom: 0 }}>
                        <td>&nbsp;</td>
                        <td>
                          Multi Brand Discount (
                          {Number.parseFloat(multiDiscountRate).toFixed(2)}%):
                        </td>
                        <td>
                          &pound;{Number.parseFloat(multiDiscountValue).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    <tr style={{ borderBottom: 0 }}>
                      <td>&nbsp;</td>
                      <td>
                        <strong>Total Cost:</strong>
                      </td>
                      <td>
                        <strong>
                          &pound;{Number.parseFloat(orderNet).toFixed(2)}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Container>

          <Container>
            <div className="row">
              <div className="col-sm-12">
                <h4 style={{ fontWeight: "bold", color: "#333" }}>
                  Notes &amp; History
                </h4>
                <table>
                  <thead>
                    <tr>
                      <th width="20%">Date</th>
                      <th width="80%" className="text-cell">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr key={item.timeStamp}>
                        <td>
                          {moment(item.timeStamp).format("DD MMM YY HH:mm")}
                        </td>
                        <td
                          className="text-cell force-wrap"
                          dangerouslySetInnerHTML={{ __html: item.comments }}
                        ></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {(hasRole(userRoles, UserRole.Admin, UserRole.NagcoMaintainer, UserRole.NagcoReceiptUnder5000, UserRole.NagcoReceiptAll)) && (
              <>
                <div className="row">
                  <div className="col-sm-12">
                    <TextArea
                      name="noteBody"
                      type="text"
                      label="Add New Note"
                      value={noteBody}
                      rows={3}
                      onChange={(name, value) => setNoteBody(value)}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12">
                    <SubmitButton
                      text="Add Note"
                      className="btn btn-secondary"
                      onClick={addNote}
                    />
                  </div>
                </div>
              </>
            )}
          </Container>
        </div>
        <div className="col-sm-4 content">
          <OrderSidebar id={id} />
        </div>
      </div>

      {reModal && (
        <>
          <div className="modal-shadow"></div>
          <div className="incident-modal">
            <div className="ms-close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                onClick={() => onCloseResendModal()}
              >
                <path
                  fillRule="evenodd"
                  d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="incident-form">
              <div className="row">
                <div className="col-sm-12">
                  <TextInput
                    name="reEmail"
                    type="email"
                    label="Recipient Email"
                    value={reEmail}
                    maxLength={45}
                    asterix={true}
                    onChange={(name, value) => setReEmail(value)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12 py-4">
                  <button
                    className="ln-c-button ln-c-button--filled"
                    onClick={onSubmitResendEmail}
                    disabled={isResending}
                  >
                    {isResending ? "Sending..." : "Submit"}
                  </button>
                </div>
              </div>
              {resendEmailError && (
                <div className="row">
                  <div className="col-sm-12">
                    <div className="alert alert-danger">{resendEmailError}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default OrderPage;
